import { LitElement, html, css, nothing } from "lit";
import { repeat } from "lit/directives/repeat.js";

export class RDAlertBox extends LitElement {
  static properties = {
    dialogOpen: { type: Boolean, state: true },
    dialogConfig: { type: Object, state: true },
    _formValues: { type: Object, state: true },
    toasts: { type: Array, state: true },
  };

  static styles = css`
    :host {
      --dialog-z-index: 1000;
      --dialog-width: 28rem;
      --toast-width: 24rem;
      --toast-spacing: 0.75rem;

      /* Base Mappings */
      --comp-primary: var(--primary-color, #007bff);
      --comp-primary-dark: var(--primary-color-dark, #0056b3);
      --comp-success: var(--success-color, #198754);
      --comp-danger: var(--danger-color, #dc3545);
      --comp-warning: var(--warning-color, #ffaa02);
      --comp-info: var(--info-color, #0dcaf0);

      --comp-font: var(--body-font-family, sans-serif);
      --comp-radius: var(--border-radius, 0.375rem);
      --comp-border: var(--border-color, #dee2e6);

      /* Light Mode Defaults */
      --comp-bg: var(--white-color, #ffffff);
      --comp-text: var(--body-color, #212529);
      --comp-text-muted: var(--secondary-color, #6c757d);
      --comp-header-color: var(--heading-color, inherit);
      --comp-input-bg: var(--white-color, #ffffff);
      --comp-footer-bg: var(--white-dark, #f3f4f6);

      font-family: var(--comp-font);
    }

    /* Dark Mode Overrides */
    :host-context([data-theme="dark"]),
    :host-context(.dark) {
      --comp-bg: var(--secondary-bg, #343a40);
      --comp-text: var(--body-color, #dee2e6);
      --comp-text-muted: var(--secondary-color, #adb5bd);
      --comp-border: var(--border-color, #495057);
      --comp-input-bg: var(--body-bg, #212529);
      --comp-footer-bg: rgba(0, 0, 0, 0.2);
    }

    /* Layout Styles */
    .backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
      z-index: var(--dialog-z-index);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.3s ease-out, visibility 0.3s;
      padding: 1rem;
    }
    .backdrop.open {
      opacity: 1;
      visibility: visible;
    }

    .dialog {
      background: var(--comp-bg);
      color: var(--comp-text);
      width: 100%;
      max-width: var(--dialog-width);
      border-radius: var(--comp-radius);
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
      display: flex;
      flex-direction: column;
      transform: scale(0.95);
      opacity: 0;
      transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
      overflow: hidden;
      border: 1px solid var(--comp-border);
    }
    .backdrop.open .dialog {
      transform: scale(1);
      opacity: 1;
    }

    .toast-container {
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      z-index: calc(var(--dialog-z-index) + 10);
      display: flex;
      flex-direction: column;
      gap: var(--toast-spacing);
      pointer-events: none;
    }

    .toast {
      width: var(--toast-width);
      max-width: calc(100vw - 3rem);
      background: var(--comp-bg);
      color: var(--comp-text);
      border-radius: var(--comp-radius);
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
      pointer-events: auto;
      border-left: 4px solid var(--toast-variant-color);
      border-top: 1px solid var(--comp-border);
      border-right: 1px solid var(--comp-border);
      border-bottom: 1px solid var(--comp-border);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: slideIn 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
      transition: transform 0.2s ease, opacity 0.2s ease;
    }
    .toast.closing {
      animation: fadeOut 0.2s ease-in forwards;
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%) scale(0.9);
        opacity: 0;
      }
      to {
        transform: translateX(0) scale(1);
        opacity: 1;
      }
    }
    @keyframes fadeOut {
      to {
        transform: translateX(20%);
        opacity: 0;
      }
    }

    .toast-content {
      padding: 1rem;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }
    .toast-body {
      flex: 1;
      min-width: 0;
    }
    .toast-title {
      font-weight: 600;
      font-size: 0.95rem;
      margin-bottom: 0.25rem;
      color: var(--comp-header-color);
    }
    .toast-message {
      font-size: 0.875rem;
      line-height: 1.4;
      color: var(--comp-text-muted);
    }
    .toast-message p {
      margin: 0 0 0.5rem 0;
    }
    .toast-message p:last-child {
      margin: 0;
    }

    .toast-close {
      background: transparent;
      border: none;
      color: var(--comp-text-muted);
      cursor: pointer;
      padding: 0.25rem;
      opacity: 0.7;
      transition: opacity 0.2s;
    }
    .toast-close:hover {
      opacity: 1;
    }

    /* Progress Bar */
    .progress-container {
      height: 3px;
      background: var(--comp-border);
      width: 100%;
    }
    .progress-bar {
      height: 100%;
      background-color: var(--toast-variant-color);
      width: 100%;
      transform-origin: left;
    }
    .progress-bar.auto-shrink {
      animation: shrink linear forwards;
      animation-duration: var(--duration);
      animation-play-state: running;
    }
    .toast:hover .progress-bar.auto-shrink {
      animation-play-state: paused;
    }
    @keyframes shrink {
      from {
        transform: scaleX(1);
      }
      to {
        transform: scaleX(0);
      }
    }
    .progress-bar.manual {
      width: var(--progress-width, 0%);
      transition: width 0.3s ease-out;
    }

    /* Dialog Internals */
    .header {
      padding: 1rem 1.25rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .title {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--comp-header-color);
    }
    .close-btn {
      background: transparent;
      border: none;
      color: var(--comp-text-muted);
      cursor: pointer;
      font-size: 1.5rem;
      line-height: 1;
    }
    .close-btn:hover {
      color: var(--comp-text);
    }

    .body {
      padding: 0 1.25rem 1.25rem 1.25rem;
      overflow-y: auto;
      max-height: 60vh;
    }
    .message {
      font-size: 0.95rem;
      line-height: 1.5;
      color: var(--comp-text-muted);
    }
    ::slotted([slot="dialog-msg"]) {
      margin: 0;
      color: inherit;
    }
    .message + .controls {
      margin-top: 1rem;
    }

    .footer {
      padding: 0.75rem 1.25rem;
      background-color: var(--comp-footer-bg);
      border-top: 1px solid var(--comp-border);
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    /* Inputs & Buttons */
    .controls {
      display: flex;
      gap: 1rem;
    }
    .controls.vertical {
      flex-direction: column;
    }
    .controls.horizontal {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-end;
    }
    .controls.grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 1rem;
    }
    .input-group {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    label {
      margin-bottom: 0.35rem;
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--comp-text-muted);
    }
    input,
    select,
    textarea {
      padding: 0.5rem 0.75rem;
      font-size: 0.95rem;
      border: 1px solid var(--comp-border);
      border-radius: var(--comp-radius);
      background: var(--comp-input-bg);
      color: var(--comp-text);
      font-family: inherit;
      width: 100%;
      box-sizing: border-box;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    input:focus,
    select:focus,
    textarea:focus {
      outline: none;
      border-color: var(--comp-primary);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--comp-primary), transparent 85%);
    }
    .input-group.checkbox {
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.25rem;
    }
    .input-group.checkbox input {
      width: 1rem;
      height: 1rem;
      margin: 0;
      cursor: pointer;
    }
    .input-group.checkbox label {
      margin: 0;
      cursor: pointer;
      color: var(--comp-text);
    }

    button.action-btn {
      padding: 0.5rem 1rem;
      border-radius: var(--comp-radius);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s;
      border: 1px solid transparent;
      line-height: 1.25;
    }
    button.primary {
      background-color: var(--comp-primary);
      color: var(--text-on-primary, #ffffff);
    }
    button.primary:hover {
      background-color: var(--comp-primary-dark);
    }
    button.secondary {
      background-color: transparent;
      color: var(--comp-text-muted);
      border-color: var(--comp-border);
    }
    button.secondary:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: var(--comp-text);
    }
    button.danger {
      background-color: var(--comp-danger);
      color: white;
    }
    button.danger:hover {
      filter: brightness(0.9);
    }
    button.success {
      background-color: var(--comp-success);
      color: white;
    }
    button.success:hover {
      filter: brightness(0.9);
    }
    button.warning {
      background-color: var(--comp-warning);
      color: #000;
    }
    button.warning:hover {
      filter: brightness(0.95);
    }

    .variant-primary {
      --toast-variant-color: var(--comp-primary);
    }
    .variant-success {
      --toast-variant-color: var(--comp-success);
    }
    .variant-danger {
      --toast-variant-color: var(--comp-danger);
    }
    .variant-warning {
      --toast-variant-color: var(--comp-warning);
    }
    .variant-info {
      --toast-variant-color: var(--comp-info);
    }
  `;

  constructor() {
    super();
    this.dialogOpen = false;
    this.dialogConfig = {};
    this.toasts = [];
    this._formValues = {};
  }

  /** Public API */
  show(config) {
    const defaults = { title: "", message: "", type: "alert", variant: "primary", layout: "vertical", inputs: [], buttons: [] };
    const finalConfig = { ...defaults, ...config };
    if (finalConfig.type === "toast") return this._addToast(finalConfig);
    return this._openDialog(finalConfig);
  }

  _resolveProgressType(config, duration) {
    if (config.progress === "auto") return "auto";
    if (config.progress && typeof config.progress === "object") return config.progress.type;
    if (!config.progress && duration > 0) return "auto";
    return "none";
  }

  updateToast(id, updates) {
    const index = this.toasts.findIndex((t) => t.id === id);
    if (index > -1) {
      const oldToast = this.toasts[index];
      const updatedToast = { ...oldToast, ...updates };
      const newDuration = updates.duration !== undefined ? updates.duration : oldToast.duration;
      const newProgressType = this._resolveProgressType(updates, newDuration);
      updatedToast.duration = newDuration;
      if (updates.progress) {
        updatedToast.progressType = newProgressType;
        if (updates.progress.value !== undefined) updatedToast.progressValue = updates.progress.value;
      }
      const shouldAutoClose = newDuration > 0 && newProgressType !== "manual";
      if (shouldAutoClose) {
        updatedToast.autoClose = true;
        if (!oldToast.autoClose || updates.duration !== undefined) {
          clearTimeout(oldToast.timerId);
          updatedToast.start = Date.now();
          updatedToast.remaining = newDuration;
          updatedToast.timerId = setTimeout(() => this._dismissToast(id, "timeout"), newDuration);
        }
      } else if (updates.duration === 0) {
        updatedToast.autoClose = false;
        clearTimeout(oldToast.timerId);
      }
      this.toasts = [...this.toasts.slice(0, index), updatedToast, ...this.toasts.slice(index + 1)];
    }
  }

  _openDialog(config) {
    if (!config.buttons || config.buttons.length === 0) {
      if (config.type === "alert") config.buttons = [{ text: "OK", variant: config.variant, action: "ok" }];
      else if (config.type === "confirm")
        config.buttons = [
          { text: "Cancel", variant: "secondary", action: "cancel" },
          { text: "Confirm", variant: config.variant, action: "confirm" },
        ];
      else if (config.type === "prompt")
        config.buttons = [
          { text: "Cancel", variant: "secondary", action: "cancel" },
          { text: "Submit", variant: config.variant, action: "submit" },
        ];
    }
    this._formValues = {};
    if (config.inputs) {
      config.inputs.forEach((input) => {
        this._formValues[input.name] = input.value || (input.type === "checkbox" ? false : "");
      });
    }
    this.dialogConfig = config;
    this.dialogOpen = true;
    return new Promise((resolve) => {
      this._dialogResolve = resolve;
    });
  }

  _closeDialog(reason) {
    this.dialogOpen = false;
    if (this._dialogResolve) {
      if (reason === "submit" || (this.dialogConfig.type === "prompt" && reason === "ok")) {
        this._dialogResolve({ action: reason, values: this._formValues });
      } else {
        this._dialogResolve({ action: reason });
      }
      this._dialogResolve = null;
    }
  }

  _addToast(config) {
    const id = `toast-${Math.random().toString(36).substr(2, 9)}`;
    const duration = config.duration || 3000;
    const progressType = this._resolveProgressType(config, duration);
    let resolveClosed;
    const closedPromise = new Promise((resolve) => (resolveClosed = resolve));
    const toast = {
      id,
      resolveClosed,
      ...config,
      duration,
      remaining: duration,
      start: Date.now(),
      autoClose: duration > 0 && progressType !== "manual",
      progressType,
      progressValue: config.progress?.value || 0,
    };
    if (toast.autoClose) {
      toast.timerId = setTimeout(() => this._dismissToast(id, "timeout"), duration);
    }
    this.toasts = [...this.toasts, toast];
    return Promise.resolve({ id, close: (reason) => this._dismissToast(id, reason || "manual"), closed: closedPromise });
  }

  _dismissToast(id, reason = "dismiss") {
    const toastIndex = this.toasts.findIndex((t) => t.id === id);
    if (toastIndex === -1) return;
    const toast = this.toasts[toastIndex];
    if (toast.resolveClosed) toast.resolveClosed({ action: reason });
    const toastEl = this.shadowRoot.getElementById(id);
    if (toastEl) {
      toastEl.classList.add("closing");
      setTimeout(() => {
        this.toasts = this.toasts.filter((t) => t.id !== id);
      }, 200);
    } else {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }
  }

  _pauseToast(id) {
    const index = this.toasts.findIndex((t) => t.id === id);
    if (index > -1 && this.toasts[index].autoClose) {
      const toast = this.toasts[index];
      clearTimeout(toast.timerId);
      toast.remaining -= Date.now() - toast.start;
    }
  }

  _resumeToast(id) {
    const index = this.toasts.findIndex((t) => t.id === id);
    if (index > -1 && this.toasts[index].autoClose) {
      const toast = this.toasts[index];
      toast.start = Date.now();
      toast.timerId = setTimeout(() => this._dismissToast(id, "timeout"), toast.remaining);
    }
  }

  _renderInput(input) {
    const isCheckbox = input.type === "checkbox";
    return html`
      <div class="input-group ${isCheckbox ? "checkbox" : ""}" style="${input.style || ""}">
        ${!isCheckbox && input.label ? html`<label>${input.label}</label>` : nothing} ${this._renderInputControl(input)} ${isCheckbox && input.label ? html`<label @click=${() => this.shadowRoot.getElementById(input.name).click()}>${input.label}</label>` : nothing}
      </div>
    `;
  }

  _renderInputControl(input) {
    const handleInput = (e, type) => {
      const val = type === "checkbox" ? e.target.checked : e.target.value;
      this._formValues = { ...this._formValues, [input.name]: val };
    };
    if (input.type === "select") {
      return html`
        <select id="${input.name}" .value=${this._formValues[input.name] || ""} @change=${(e) => handleInput(e, "select")}>
          ${input.options.map((opt) => html`<option value="${opt.value}">${opt.label}</option>`)}
        </select>
      `;
    } else if (input.type === "textarea") {
      return html` <textarea id="${input.name}" rows="${input.rows || 3}" .value=${this._formValues[input.name] || ""} @input=${(e) => handleInput(e, "textarea")}></textarea> `;
    } else if (input.type === "checkbox") {
      return html` <input type="checkbox" id="${input.name}" .checked=${!!this._formValues[input.name]} @change=${(e) => handleInput(e, "checkbox")} /> `;
    }
    return html` <input type="${input.type || "text"}" id="${input.name}" placeholder="${input.placeholder || ""}" .value=${this._formValues[input.name] || ""} @input=${(e) => handleInput(e, input.type)} /> `;
  }

  render() {
    return html`
      <div
        class="backdrop ${this.dialogOpen ? "open" : ""}"
        @click=${(e) => {
          if (e.target === e.currentTarget && this.dialogConfig.backdropDismiss !== false) {
            this._closeDialog("backdrop");
          }
        }}
      >
        <div class="dialog">
          ${this.dialogConfig.type !== "toast"
            ? html`
                <div class="header">
                  <h3 class="title">${this.dialogConfig.title}</h3>
                  ${this.dialogConfig.type !== "alert"
                    ? html`
                        <button class="close-btn" @click=${() => this._closeDialog("close")}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      `
                    : nothing}
                </div>
              `
            : nothing}

          <div class="body">
            <div class="message">
              <slot name="dialog-msg"></slot>
            </div>
            ${this.dialogConfig.inputs && this.dialogConfig.inputs.length > 0 ? html` <div class="controls ${this.dialogConfig.layout || "vertical"}">${this.dialogConfig.inputs.map((i) => this._renderInput(i))}</div> ` : nothing}
          </div>

          <div class="footer">${this.dialogConfig.buttons?.map((btn) => html` <button class="action-btn ${btn.variant || "secondary"}" @click=${() => this._closeDialog(btn.action || btn.text.toLowerCase())}>${btn.text}</button> `)}</div>
        </div>
      </div>

      <div class="toast-container">
        ${repeat(
          this.toasts,
          (t) => t.id,
          (toast) => this._renderToast(toast)
        )}
      </div>
    `;
  }

  _renderToast(toast) {
    let variantClass = `variant-${toast.variant || "primary"}`;
    const progressKey = `${toast.progressType}-${toast.duration}`;
    return html`
      <div id="${toast.id}" class="toast ${variantClass}" style="--duration: ${toast.duration}ms" @mouseenter=${() => this._pauseToast(toast.id)} @mouseleave=${() => this._resumeToast(toast.id)}>
        <div class="toast-content">
          ${this._getIconForVariant(toast.variant)}
          <div class="toast-body">
            ${toast.title ? html`<div class="toast-title">${toast.title}</div>` : nothing}
            <div class="toast-message">${unsafeHTML(toast.message)}</div>
          </div>
          ${toast.closeButton !== false
            ? html`
                <button class="toast-close" @click=${() => this._dismissToast(toast.id, "close-btn")}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              `
            : nothing}
        </div>
        ${toast.progressType !== "none"
          ? html`
              <div class="progress-container">
                <div class="progress-bar ${toast.progressType === "auto" ? "auto-shrink" : "manual"}" key="${progressKey}" style="${toast.progressType === "manual" ? `width: ${toast.progressValue}%` : ""}"></div>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  _getIconForVariant(variant) {
    const styles = "width:22px; height:22px; flex-shrink:0; color:var(--toast-variant-color);";
    switch (variant) {
      case "success":
        return html`<svg style="${styles}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>`;
      case "danger":
        return html`<svg style="${styles}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>`;
      case "warning":
        return html`<svg style="${styles}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>`;
      case "info":
        return html`<svg style="${styles}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>`;
      default:
        return nothing;
    }
  }
}
customElements.define("rd-alert", RDAlertBox);
